import { useEffect, useMemo, useState } from "react";
import { createRoot, type Root } from "react-dom/client";
import kuiiLogoColourUrl from "./kuii-logo-c.png";
import dashboardPreviewUrl from "./dashscreen.png";
import "../index.css";

type Language = "en" | "ja";

declare global {
  interface Window {
    __kuiiLandingRoot__?: Root;
  }
}

const defaultLanguage: Language = "en";
const languageStorageKey = "kuii.site.language";

const landingCopy = {
  en: {
    metaTitle: "Kuii | Tickets, calendar, customers, expenses, and KB in one place",
    languageSelectorLabel: "Language",
    languageNames: { en: "English", ja: "日本語" },
    actions: {
      documentation: "Documentation",
      api: "API",
      signIn: "Sign in",
      startFree: "Start free",
      home: "Home",
      apiReference: "API reference",
      termsOfService: "Terms of Service",
      privacyPolicy: "Privacy Policy",
      refundPolicy: "Refund Policy",
      popular: "Popular",
      included: "Included",
    },
    sectionLinks: [
      { label: "Overview", href: "#overview" },
      { label: "Why teams switch", href: "#why-teams-switch" },
      { label: "Pricing", href: "#pricing" },
      { label: "Savings calculator", href: "#savings-calculator" },
    ],
    hero: {
      badge: "One workspace to run your business operations",
      title: "Tickets, calendar, customers, expenses, and knowledge base in one low-cost platform.",
      description:
        "Kuii replaces tool sprawl with a single workspace for support tickets, team scheduling, customer records, expense tracking, and self-serve docs so running day-to-day operations is faster and cheaper.",
      subtext: "Create your workspace in minutes.",
      previewAlt: "Kuii console dashboard showing ticket volume, calendar schedule, and service status",
      checklist: ["No credit card to create an account", "No trial deadline", "No per-seat fees"],
      featureCards: [
        ["Ticketing with full context", "Handle customer requests alongside account details, the products and services they use, and relationship history."],
        ["Team calendar built in", "Plan coverage, shifts, and time off in the same workspace your team runs on."],
        ["Expenses and vendors", "Track spending with vendors to keep tabs on costs."],
        ["Knowledge base included", "Publish customer help articles and internal playbooks without buying another product."],
      ],
    },
    whyTeamsSwitch: {
      eyebrow: "Why teams switch",
      title: "Fewer tools. One place to run the business.",
      description:
        "Kuii is built for businesses that want tickets, scheduling, customer records, expenses, and documentation connected in one product instead of spread across multiple subscriptions.",
      items: [
        ["Stop paying for overlap", "Skip separate systems for ticketing, scheduling, expenses, and knowledge base publishing."],
        ["Keep operations visible", "Run your team calendar for shifts, ownership, and planned time off without leaving the workspace."],
        ["Keep every record connected", "Tie customers, tickets, products, services, and docs together in the same workflow."],
        ["Pricing stays simple", "Start free, skip per-seat pricing, and only pay straightforward usage fees as you grow."],
      ],
    },
    pricing: {
      eyebrow: "Pricing",
      title: "Full product access at every price point.",
      description:
        "Every plan includes the same core platform. The only things that change are how much usage is included and how quickly you can reach support.",
      note: "No feature gating. Just more allowance and stronger support as you grow.",
      includedAllowance: "Included allowance",
      supportLabel: "Support",
      retentionLabel: "Data retention / rollbacks",
      table: {
        whatsIncluded: "What's included",
        corePlatform: "Core platform",
        allowanceAndSupport: "Allowance and support",
      },
      plans: [
        {
          name: "Free",
          price: "$0",
          cadence: "/month",
          description: "Best for trying Kuii with a real workflow before you pay.",
          allowance: "100 tickets · 10 Knowledge base articles · 1,000 logs",
          support: "Community support",
          retention: "Rollbacks up to 7 days",
          ctaLabel: "Start free",
          featured: false,
        },
        {
          name: "Solo",
          price: "$15",
          cadence: "/month",
          description: "For solo operators who need more room with simple overages for growth.",
          allowance: "5,000 tickets · 100 Knowledge base articles · 100,000 logs",
          support: "Standard email support within 1 business day",
          retention: "Rollbacks up to 30 days",
          ctaLabel: "Choose Solo",
          featured: false,
        },
        {
          name: "Growth",
          price: "$49",
          cadence: "/month",
          description: "For teams that need more volume with simple overages.",
          allowance: "20,000 tickets · 500 Knowledge base articles · 500,000 logs",
          support: "Standard email support within 1 business day",
          retention: "Rollbacks up to 30 days",
          ctaLabel: "Choose Growth",
          featured: true,
        },
      ],
      coreFeatures: [
        "Ticketing workspace",
        "Team calendar and coverage planning",
        "Expense and vendor tracking",
        "Knowledge base publishing",
        "Products and services linked to customers",
        "Internal notes and team playbooks",
        "Role-based access",
        "API access",
      ],
      allowanceRows: [
        {
          label: "Ticket limit",
          values: [
            { primary: "100 included", secondary: "No additional tickets" },
            { primary: "5,000 included", secondary: "+$5 per additional 5,000" },
            { primary: "20,000 included", secondary: "+$5 per additional 5,000" },
          ],
        },
        {
          label: "KB article limit",
          values: [
            { primary: "10 included", secondary: "No additional articles" },
            { primary: "100 included", secondary: "+$1 per additional 100" },
            { primary: "500 included", secondary: "+$1 per additional 100" },
          ],
        },
        {
          label: "Logs stored",
          values: [
            { primary: "1,000 included", secondary: "No additional logs" },
            { primary: "100,000 included", secondary: "+$1 per additional 100,000" },
            { primary: "500,000 included", secondary: "+$1 per additional 100,000" },
          ],
        },
        {
          label: "Users",
          values: [
            { primary: "Unlimited", secondary: "" },
            { primary: "1 user", secondary: "Built for solo operators" },
            { primary: "Unlimited", secondary: "" },
          ],
        },
        {
          label: "Customers",
          values: [
            { primary: "Unlimited", secondary: "" },
            { primary: "Unlimited", secondary: "" },
            { primary: "Unlimited", secondary: "" },
          ],
        },
        {
          label: "Products",
          values: [
            { primary: "Unlimited", secondary: "" },
            { primary: "Unlimited", secondary: "" },
            { primary: "Unlimited", secondary: "" },
          ],
        },
        {
          label: "Categories",
          values: [
            { primary: "Unlimited", secondary: "" },
            { primary: "Unlimited", secondary: "" },
            { primary: "Unlimited", secondary: "" },
          ],
        },
        {
          label: "Support",
          values: [
            { primary: "Community", secondary: "" },
            { primary: "Standard email", secondary: "Within 1 business day" },
            { primary: "Standard email", secondary: "Within 1 business day" },
          ],
        },
        {
          label: "Data retention / rollbacks",
          values: [
            { primary: "7-day rollback window", secondary: "Restore data changes from the last 7 days" },
            { primary: "30-day rollback window", secondary: "Restore data changes from the last 30 days" },
            { primary: "30-day rollback window", secondary: "Restore data changes from the last 30 days" },
          ],
        },
      ],
    },
    calculator: {
      eyebrow: "Cost savings calculator",
      title: "Compare Kuii against the common alternatives.",
      description:
        "Set your team size and expected volume to estimate monthly spend with Kuii versus Zendesk, Intercom, and Help Scout.",
      modelTitle: "Model your support setup",
      modelDescription:
        "Kuii pricing changes with ticket, article, and log allowance, while the alternative estimates below are driven by per-agent list pricing.",
      fields: [
          { label: "Support agents", helper: "How many teammates need support access", min: 1, max: 49, step: 1 },
          { label: "Tickets", helper: "Used to price Kuii Free, Solo, or Growth + overages", min: 0, max: 100000, step: 500 },
          { label: "Knowledge base articles", helper: "Published knowledge base article count", min: 0, max: 5000, step: 25 },
          { label: "Logs stored", helper: "Total audit and access logs retained", min: 0, max: 1000000, step: 10000 },
      ],
      currentEstimate: "Current Kuii estimate",
      comparisonTitle: "Estimated monthly comparison",
      providerMonthlyCost: "Estimated monthly cost",
      providerSavings: "Estimated savings with Kuii",
      providerFallbackName: "alternative provider",
      disclaimer:
        "Estimates use public list pricing for comparable plans and are intended for directional comparison only. They exclude discounts, taxes, negotiated contracts, and optional add-ons. Pricing compared as of July 1, 2026.",
      providers: [
        { name: "Zendesk Support Team", monthlySeatPrice: 19, detail: "$19 per agent / month", note: "Lowest paid public tier, billed annually; email-first support plan" },
        { name: "Intercom Essential", monthlySeatPrice: 29, detail: "$29 per seat / month", note: "Lowest paid public tier, billed annually, excluding optional AI outcomes" },
        { name: "Help Scout Standard", monthlySeatPrice: 25, detail: "$25 per user / month", note: "Lowest paid public tier, billed annually" },
      ],
    },
    footer: {
      tagline: "One workspace for tickets, calendar, customers, expenses, and knowledge base.",
    },
  },
  ja: {
    metaTitle: "Kuii | チケット、カレンダー、顧客、経費、ナレッジベースをひとつに",
    languageSelectorLabel: "言語",
    languageNames: { en: "English", ja: "日本語" },
    actions: {
      documentation: "ドキュメント",
      api: "API",
      signIn: "ログイン",
      startFree: "無料で始める",
      home: "ホーム",
      apiReference: "APIリファレンス",
      termsOfService: "利用規約",
      privacyPolicy: "プライバシーポリシー",
      refundPolicy: "返金ポリシー",
      popular: "人気",
      included: "含まれます",
    },
    sectionLinks: [
      { label: "概要", href: "#overview" },
      { label: "選ばれる理由", href: "#why-teams-switch" },
      { label: "料金", href: "#pricing" },
      { label: "料金比較", href: "#savings-calculator" },
    ],
    hero: {
      badge: "ビジネス運営をひとつのワークスペースで",
      title: "チケット、カレンダー、顧客、経費、ナレッジベースを、低コストなひとつのプラットフォームに。",
      description:
        "Kuii は、サポートチケット、チームのスケジュール管理、顧客情報、経費管理、セルフサービスのドキュメントをひとつのワークスペースにまとめ、日々の業務運営をより速く、より低コストにします。",
      subtext: "数分でワークスペースを作成できます。",
      previewAlt: "チケット件数、カレンダーの予定、サービス稼働状況を表示する Kuii コンソールのダッシュボード",
      checklist: ["アカウント作成にクレジットカード不要", "期限付きトライアルなし", "ユーザー課金なし"],
      featureCards: [
        ["チケットと顧客情報を一画面で", "問い合わせを、アカウント情報、顧客が利用中の商品・サービス、関係履歴と並べて対応できます。"],
        ["チームカレンダー込み", "当番、シフト、休暇予定を、チームが日々使うワークスペースの中で管理できます。"],
        ["経費と取引先の管理", "取引先ごとの支出を記録してコストを把握できます。"],
        ["ナレッジベース込み", "別製品を増やさずに、顧客向けヘルプ記事や社内プレイブックを公開できます。"],
      ],
    },
    whyTeamsSwitch: {
      eyebrow: "乗り換える理由",
      title: "ツールを減らし、業務をひとつの場所で。",
      description:
        "Kuii は、チケット、スケジュール管理、顧客情報、経費、ドキュメントを複数の契約に分散させず、ひとつの製品でつなげたいビジネスのために作られています。",
      items: [
        ["重複コストを止める", "チケッティング、スケジュール管理、経費、ナレッジベース公開を別々のシステムで持つ必要がありません。"],
        ["業務を見える化", "シフト、担当、休暇予定を、ワークスペースから離れずにチームカレンダーで管理できます。"],
        ["すべての記録をつなげる", "顧客、チケット、商品、サービス、ドキュメントを同じワークフローの中でひとつにまとめられます。"],
        ["料金はシンプル", "無料で始め、ユーザー課金を避け、成長した分だけ分かりやすく支払えます。"],
      ],
    },
    pricing: {
      eyebrow: "料金",
      title: "どのプランでも製品全体を使えます。",
      description:
        "すべてのプランで同じコアプラットフォームを利用できます。変わるのは、含まれる利用枠とサポート到達までの速さだけです。",
      note: "機能制限はありません。成長に合わせて利用枠とサポートが増えるだけです。",
      includedAllowance: "含まれる利用枠",
      supportLabel: "サポート",
      retentionLabel: "データ保持 / ロールバック",
      table: {
        whatsIncluded: "含まれるもの",
        corePlatform: "コアプラットフォーム",
        allowanceAndSupport: "利用枠とサポート",
      },
      plans: [
        {
          name: "Free",
          price: "$0",
          cadence: "/月",
          description: "有料化の前に、実運用に近い形で Kuii を試したいチーム向け。",
          allowance: "チケット100件 · ナレッジベース記事10本 · ログ1,000件",
          support: "コミュニティ + メールサポート（2営業日以内）",
          retention: "最大7日間のロールバック",
          ctaLabel: "無料で始める",
          featured: false,
        },
        {
          name: "Solo",
          price: "$15",
          cadence: "/月",
          description: "ひとり運営向け。チーム規模に上がる前の余裕と、成長に合わせやすいシンプルな超過料金を備えています。",
          allowance: "チケット5,000件 · ナレッジベース記事100本 · ログ100,000件",
          support: "標準メールサポート（1営業日以内）",
          retention: "最大30日間のロールバック",
          ctaLabel: "Solo を選ぶ",
          featured: false,
        },
        {
          name: "Growth",
          price: "$49",
          cadence: "/月",
          description: "より多いボリュームが必要なチーム向け。超過料金もシンプルです。",
          allowance: "チケット20,000件 · ナレッジベース記事500本 · ログ500,000件",
          support: "標準メールサポート（1営業日以内）",
          retention: "最大30日間のロールバック",
          ctaLabel: "Growth を選ぶ",
          featured: true,
        },
      ],
      coreFeatures: [
        "チケッティングワークスペース",
        "チームカレンダーと担当計画",
        "経費と取引先の管理",
        "ナレッジベース公開",
        "顧客に紐づく商品・サービス",
        "社内メモとチームプレイブック",
        "ロールベースアクセス",
        "APIアクセス",
      ],
      allowanceRows: [
        {
          label: "チケット上限",
          values: [
            { primary: "100件込み", secondary: "追加チケットなし" },
            { primary: "5,000件込み", secondary: "5,000件ごとに +$5" },
            { primary: "20,000件込み", secondary: "5,000件ごとに +$5" },
          ],
        },
        {
          label: "KB記事上限",
          values: [
            { primary: "10本込み", secondary: "追加記事なし" },
            { primary: "100本込み", secondary: "100本ごとに +$1" },
            { primary: "500本込み", secondary: "100本ごとに +$1" },
          ],
        },
        {
          label: "保存ログ数",
          values: [
            { primary: "1,000件込み", secondary: "追加ログなし" },
            { primary: "100,000件込み", secondary: "100,000件ごとに +$1" },
            { primary: "500,000件込み", secondary: "100,000件ごとに +$1" },
          ],
        },
        {
          label: "ユーザー数",
          values: [
            { primary: "無制限", secondary: "" },
            { primary: "1ユーザー", secondary: "ひとり運営向け" },
            { primary: "無制限", secondary: "" },
          ],
        },
        {
          label: "顧客数",
          values: [
            { primary: "無制限", secondary: "" },
            { primary: "無制限", secondary: "" },
            { primary: "無制限", secondary: "" },
          ],
        },
        {
          label: "製品数",
          values: [
            { primary: "無制限", secondary: "" },
            { primary: "無制限", secondary: "" },
            { primary: "無制限", secondary: "" },
          ],
        },
        {
          label: "カテゴリ数",
          values: [
            { primary: "無制限", secondary: "" },
            { primary: "無制限", secondary: "" },
            { primary: "無制限", secondary: "" },
          ],
        },
        {
          label: "サポート",
          values: [
            { primary: "コミュニティ + メール", secondary: "2営業日以内" },
            { primary: "標準メール", secondary: "1営業日以内" },
            { primary: "標準メール", secondary: "1営業日以内" },
          ],
        },
        {
          label: "データ保持 / ロールバック",
          values: [
            { primary: "7日間のロールバック", secondary: "過去7日間のデータ変更を復元" },
            { primary: "30日間のロールバック", secondary: "過去30日間のデータ変更を復元" },
            { primary: "30日間のロールバック", secondary: "過去30日間のデータ変更を復元" },
          ],
        },
      ],
    },
    calculator: {
      eyebrow: "コスト削減シミュレーター",
      title: "Kuii と主要な代替サービスを比較。",
      description:
        "チーム規模と想定ボリュームを設定して、Kuii と Zendesk、Intercom、Help Scout の月額費用を見積もれます。",
      modelTitle: "サポート体制を試算",
      modelDescription:
        "Kuii の料金はチケット数、記事数、保存ログ数の利用枠に応じて変わります。一方、下の代替サービス見積もりは公開されている担当者単価ベースです。",
      fields: [
          { label: "サポート担当者数", helper: "サポート画面を使う人数", min: 1, max: 49, step: 1 },
          { label: "チケット数", helper: "Kuii の Free、Solo、Growth と超過料金の計算に使われます", min: 0, max: 100000, step: 500 },
          { label: "ナレッジベース記事数", helper: "公開するナレッジベース記事数", min: 0, max: 5000, step: 25 },
          { label: "保存ログ数", helper: "保持している監査ログとアクセスログの合計件数", min: 0, max: 1000000, step: 10000 },
      ],
      currentEstimate: "現在の Kuii 見積もり",
      comparisonTitle: "月額見積もり比較",
      providerMonthlyCost: "想定月額費用",
      providerSavings: "Kuii にした場合の想定差額",
      providerFallbackName: "他サービス",
      disclaimer:
        "見積もりは公開料金に基づく概算です。割引、税金、個別契約、オプション追加料金は含みません。料金の比較時点は2026年7月1日です。",
      providers: [
        { name: "Zendesk Support Team", monthlySeatPrice: 19, detail: "$19 / エージェント / 月", note: "公開されている最安の有料プランを想定（年払い、メール中心のサポートプラン）" },
        { name: "Intercom Essential", monthlySeatPrice: 29, detail: "$29 / 席 / 月", note: "公開されている最安の有料プランを想定（年払い、任意の AI 機能は除く）" },
        { name: "Help Scout Standard", monthlySeatPrice: 25, detail: "$25 / ユーザー / 月", note: "公開されている最安の有料プランを想定（年払い）" },
      ],
    },
    footer: {
      tagline: "チケット、カレンダー、顧客、経費、ナレッジベースをひとつのワークスペースに。",
    },
  },
} as const;

function getPublicConfig() {
  return {
    homeHref: window.__KUII_PUBLIC_CONFIG__?.homeHref ?? "./",
    documentationHref: window.__KUII_PUBLIC_CONFIG__?.documentationHref ?? "./documentation/",
    apiHref: window.__KUII_PUBLIC_CONFIG__?.apiHref ?? "./api/",
    loginHref: window.__KUII_PUBLIC_CONFIG__?.loginHref ?? "./login/",
    signupHref: window.__KUII_PUBLIC_CONFIG__?.signupHref ?? "./signup/",
    termsHref: window.__KUII_PUBLIC_CONFIG__?.termsHref ?? "./terms/",
    privacyHref: window.__KUII_PUBLIC_CONFIG__?.privacyHref ?? "./privacy/",
    refundPolicyHref: window.__KUII_PUBLIC_CONFIG__?.refundPolicyHref ?? "./refund-policy/",
  };
}

function navigateTo(href: string) {
  window.location.href = href;
}

function isLanguage(value: string | null | undefined): value is Language {
  return value === "en" || value === "ja";
}

function readStoredLanguage() {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const value = window.localStorage.getItem(languageStorageKey);
    return isLanguage(value) ? value : null;
  } catch {
    return null;
  }
}

function writeStoredLanguage(language: Language) {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(languageStorageKey, language);
  } catch {
    // Ignore storage failures.
  }
}

function getLanguageFromQueryParam() {
  if (typeof window === "undefined") {
    return null;
  }

  const params = new URLSearchParams(window.location.search);
  const value = params.get("lang");
  return isLanguage(value) ? value : null;
}

function detectBrowserLanguage(): Language {
  if (typeof window === "undefined") {
    return defaultLanguage;
  }

  const candidates = [...(window.navigator.languages ?? []), window.navigator.language].filter((value): value is string => Boolean(value));
  return candidates.some(value => value.toLowerCase().startsWith("ja")) ? "ja" : defaultLanguage;
}

function getInitialLanguage(): Language {
  return getLanguageFromQueryParam() ?? readStoredLanguage() ?? detectBrowserLanguage();
}

function syncLanguagePreference(language: Language) {
  if (typeof window === "undefined") {
    return;
  }

  writeStoredLanguage(language);

  const nextUrl = new URL(window.location.href);
  if (language === defaultLanguage) {
    nextUrl.searchParams.delete("lang");
  } else {
    nextUrl.searchParams.set("lang", language);
  }

  window.history.replaceState({}, "", `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`);
}

function calculateKuiiMonthlyCost(agentCount: number, ticketCount: number, articleCount: number, logCount: number) {
  if (ticketCount <= 100 && articleCount <= 10 && logCount <= 1000) {
    return 0;
  }

  if (agentCount <= 1) {
    const solopreneurTicketOverageBlocks = Math.max(0, Math.ceil((ticketCount - 5000) / 5000));
    const solopreneurArticleOverageBlocks = Math.max(0, Math.ceil((articleCount - 100) / 100));
    const solopreneurLogOverageBlocks = Math.max(0, Math.ceil((logCount - 100000) / 100000));
    const solopreneurCost = 15 + solopreneurTicketOverageBlocks * 5 + solopreneurArticleOverageBlocks + solopreneurLogOverageBlocks;

    if (ticketCount <= 5000 && articleCount <= 100 && logCount <= 100000) {
      return solopreneurCost;
    }

    const growthTicketOverageBlocks = Math.max(0, Math.ceil((ticketCount - 20000) / 5000));
    const growthArticleOverageBlocks = Math.max(0, Math.ceil((articleCount - 500) / 100));
    const growthLogOverageBlocks = Math.max(0, Math.ceil((logCount - 500000) / 100000));
    const growthCost = 49 + growthTicketOverageBlocks * 5 + growthArticleOverageBlocks + growthLogOverageBlocks;

    return Math.min(solopreneurCost, growthCost);
  }

  const ticketOverageBlocks = Math.max(0, Math.ceil((ticketCount - 20000) / 5000));
  const articleOverageBlocks = Math.max(0, Math.ceil((articleCount - 500) / 100));
  const logOverageBlocks = Math.max(0, Math.ceil((logCount - 500000) / 100000));

  return 49 + ticketOverageBlocks * 5 + articleOverageBlocks + logOverageBlocks;
}

function formatCurrency(amount: number, language: Language) {
  return new Intl.NumberFormat(language === "ja" ? "ja-JP" : "en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatCount(value: number, language: Language) {
  return new Intl.NumberFormat(language === "ja" ? "ja-JP" : "en-US").format(value);
}

function getEstimateDescription(language: Language, kuiiMonthlyCost: number, agentCount: number) {
  if (language === "ja") {
    if (kuiiMonthlyCost === 0) return "この規模なら無料プラン内に収まります。";
    if (agentCount <= 1 && kuiiMonthlyCost === 15) return "この規模なら Solo プラン内に収まります。";
    if (agentCount <= 1 && kuiiMonthlyCost < 49) return "この見積もりは、Solo プランに、含まれる枠を超えたチケット数・記事数・ログ数の超過料金を加えたものです。";
    return "この見積もりは、Growth プランに、含まれる枠を超えたチケット数・記事数・ログ数の超過料金を加えたものです。";
  }

  if (kuiiMonthlyCost === 0) return "This workload still fits inside the free plan.";
  if (agentCount <= 1 && kuiiMonthlyCost === 15) return "This workload fits inside the Solo plan.";
  if (agentCount <= 1 && kuiiMonthlyCost < 49) return "This estimate uses the Solo plan plus any ticket, article, or log overages above the included allowance.";
  return "This estimate uses the Growth plan plus any ticket, article, or log overages above the included allowance.";
}

function getComparisonDescription(language: Language, agentCount: number, ticketCount: number, articleCount: number, logCount: number) {
  const formattedAgentCount = formatCount(agentCount, language);
  const formattedTicketCount = formatCount(ticketCount, language);
  const formattedArticleCount = formatCount(articleCount, language);
  const formattedLogs = formatCount(logCount, language);

  if (language === "ja") {
    return `${formattedAgentCount}人の担当者、月間${formattedTicketCount}件のチケット、${formattedArticleCount}本のナレッジベース記事、保存ログ数 ${formattedLogs} 件を前提に試算しています。`;
  }

  return `Based on ${formattedAgentCount} support agents, ${formattedTicketCount} monthly tickets, ${formattedArticleCount} Knowledge base articles, and ${formattedLogs} stored logs.`;
}

function getHighestSavingsText(language: Language, savings: number, providerName: string) {
  const formattedSavings = formatCurrency(savings, language);
  return language === "ja"
    ? `現在もっとも大きい想定差額: ${formattedSavings}/月（${providerName}比）`
    : `Highest modeled savings right now: ${formattedSavings}/month versus ${providerName}.`;
}

function LandingPage() {
  const config = getPublicConfig();
  const [language, setLanguage] = useState<Language>(getInitialLanguage);
  const [agentCount, setAgentCount] = useState(6);
  const [ticketCount, setTicketCount] = useState(12000);
  const [articleCount, setArticleCount] = useState(250);
  const [logCount, setLogCount] = useState(100000);
  const copy = landingCopy[language];

  useEffect(() => {
    document.documentElement.lang = language;
    document.title = copy.metaTitle;
    syncLanguagePreference(language);
  }, [copy.metaTitle, language]);

  const kuiiMonthlyCost = useMemo(() => calculateKuiiMonthlyCost(agentCount, ticketCount, articleCount, logCount), [agentCount, articleCount, ticketCount, logCount]);

  const providerEstimates = useMemo(
    () => copy.calculator.providers.map(provider => ({ ...provider, monthlyCost: provider.monthlySeatPrice * agentCount, savings: Math.max(0, provider.monthlySeatPrice * agentCount - kuiiMonthlyCost) })),
    [agentCount, copy.calculator.providers, kuiiMonthlyCost],
  );

  const biggestSavings = providerEstimates.reduce<(typeof providerEstimates)[number] | null>((best, provider) => (!best || provider.savings > best.savings ? provider : best), null) ?? {
    name: copy.calculator.providerFallbackName,
    monthlySeatPrice: 0,
    detail: "",
    note: "",
    monthlyCost: 0,
    savings: 0,
  };

  const calculatorFields = [
    { ...copy.calculator.fields[0], value: agentCount, onChange: setAgentCount },
    { ...copy.calculator.fields[1], value: ticketCount, onChange: setTicketCount },
    { ...copy.calculator.fields[2], value: articleCount, onChange: setArticleCount },
    { ...copy.calculator.fields[3], value: logCount, onChange: setLogCount },
  ] as const;

  return (
    <main className="min-h-screen">
      <section className="relative overflow-hidden border-b border-white/60 bg-[radial-gradient(circle_at_top_left,rgba(108,219,170,0.26),transparent_24%),linear-gradient(180deg,#fcfcfd_0%,#f8fafc_100%)]">
        <div className="mx-auto flex max-w-7xl flex-col gap-10 px-6 py-6 sm:px-8 lg:px-10">
          <header className="flex items-center justify-between gap-4">
            <div>
              <img src={kuiiLogoColourUrl} alt="Kuii" className="h-10 w-auto sm:h-12" />
            </div>

            <div className="flex flex-wrap items-center justify-end gap-3">
              <label className="flex items-center gap-2 rounded-full border border-white/80 bg-white/75 px-3 py-2 text-sm font-semibold text-[#475467] shadow-[0_10px_30px_rgba(15,23,42,0.06)] backdrop-blur-sm">
                <span aria-hidden="true" className="text-[#344054]">
                  <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="currentColor">
                    <path d="M325-111.5q-73-31.5-127.5-86t-86-127.5Q80-398 80-480.5t31.5-155q31.5-72.5 86-127t127.5-86Q398-880 480.5-880t155 31.5q72.5 31.5 127 86t86 127Q880-563 880-480.5T848.5-325q-31.5 73-86 127.5t-127 86Q563-80 480.5-80T325-111.5ZM480-162q26-36 45-75t31-83H404q12 44 31 83t45 75Zm-104-16q-18-33-31.5-68.5T322-320H204q29 50 72.5 87t99.5 55Zm208 0q56-18 99.5-55t72.5-87H638q-9 38-22.5 73.5T584-178ZM170-400h136q-3-20-4.5-39.5T300-480q0-21 1.5-40.5T306-560H170q-5 20-7.5 39.5T160-480q0 21 2.5 40.5T170-400Zm216 0h188q3-20 4.5-39.5T580-480q0-21-1.5-40.5T574-560H386q-3 20-4.5 39.5T380-480q0 21 1.5 40.5T386-400Zm268 0h136q5-20 7.5-39.5T800-480q0-21-2.5-40.5T790-560H654q3 20 4.5 39.5T660-480q0 21-1.5 40.5T654-400Zm-16-240h118q-29-50-72.5-87T584-782q18 33 31.5 68.5T638-640Zm-234 0h152q-12-44-31-83t-45-75q-26 36-45 75t-31 83Zm-200 0h118q9-38 22.5-73.5T376-782q-56 18-99.5 55T204-640Z" />
                  </svg>
                </span>
                <select
                  aria-label={copy.languageSelectorLabel}
                  value={language}
                  onChange={event => {
                    setLanguage(event.target.value as Language);
                  }}
                  className="bg-transparent font-semibold text-[#101828] outline-none"
                >
                  <option value="en">{copy.languageNames.en}</option>
                  <option value="ja">{copy.languageNames.ja}</option>
                </select>
              </label>

              <nav className="hidden items-center gap-1 rounded-full border border-white/80 bg-white/75 p-1 shadow-[0_10px_30px_rgba(15,23,42,0.06)] backdrop-blur-sm lg:flex">
                {copy.sectionLinks.map(link => (
                  <a key={link.href} href={link.href} className="rounded-full px-4 py-2 text-sm font-semibold text-[#475467] transition hover:bg-white hover:text-[#101828]">
                    {link.label}
                  </a>
                ))}
                <a href={config.documentationHref} className="rounded-full px-4 py-2 text-sm font-semibold text-[#475467] transition hover:bg-white hover:text-[#101828]">
                  {copy.actions.documentation}
                </a>
                <a href={config.apiHref} className="rounded-full px-4 py-2 text-sm font-semibold text-[#475467] transition hover:bg-white hover:text-[#101828]">
                  {copy.actions.api}
                </a>
              </nav>

              <nav className="flex items-center gap-3">
                <button type="button" onClick={() => navigateTo(config.loginHref)} className="brand-primary-subtle-button rounded-full px-4 py-2 text-sm font-semibold">
                  {copy.actions.signIn}
                </button>
                <button type="button" onClick={() => navigateTo(config.signupHref)} className="brand-primary-button rounded-full px-4 py-2 text-sm font-semibold">
                  {copy.actions.startFree}
                </button>
              </nav>
            </div>
          </header>

          <div className="pb-8 pt-4 lg:pb-14">
            <section id="overview">
              <div className="inline-flex rounded-full border border-white/70 bg-white/80 px-4 py-2 text-xs font-semibold uppercase tracking-[0.24em] text-[#475467] shadow-[0_10px_30px_rgba(15,23,42,0.06)]">
                {copy.hero.badge}
              </div>

              <h1 className="font-headline mt-6 max-w-4xl text-5xl font-extrabold tracking-[-0.06em] text-[#101828] sm:text-6xl">{copy.hero.title}</h1>
              <p className="mt-6 max-w-2xl text-lg leading-8 text-[#475467]">{copy.hero.description}</p>

              <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
                <button type="button" onClick={() => navigateTo(config.signupHref)} className="brand-primary-button rounded-[22px] px-5 py-3 text-sm font-bold uppercase tracking-[0.2em]">
                  {copy.actions.startFree}
                </button>
                <p className="text-sm font-medium text-[#667085] sm:ml-1">{copy.hero.subtext}</p>
              </div>

              <div className="mt-6 grid max-w-2xl gap-3 rounded-[24px] border border-white/80 bg-white/70 p-4 shadow-[0_16px_40px_rgba(15,23,42,0.05)] backdrop-blur-sm sm:grid-cols-3">
                {copy.hero.checklist.map(item => (
                  <div key={item} className="flex items-center gap-3 rounded-[18px] bg-white/80 px-3 py-3 text-sm font-semibold text-[#344054]">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#dcfae6] text-sm text-[#027a48] shadow-inner">✓</span>
                    <span>{item}</span>
                  </div>
                ))}
              </div>

              <div className="mt-10 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                {copy.hero.featureCards.map(([title, detail]) => (
                  <article key={title} className="rounded-[28px] border border-white/80 bg-white/75 p-5 shadow-[0_20px_50px_rgba(15,23,42,0.05)] backdrop-blur-sm">
                    <p className="text-sm font-bold text-[#101828]">{title}</p>
                    <p className="mt-2 text-sm leading-6 text-[#667085]">{detail}</p>
                  </article>
                ))}
              </div>

              <div className="mt-12 overflow-hidden rounded-[36px] border border-white/80 bg-white/70 p-3 shadow-[0_30px_90px_rgba(15,23,42,0.12)] backdrop-blur-sm sm:p-4">
                <img
                  src={dashboardPreviewUrl}
                  alt={copy.hero.previewAlt}
                  className="w-full rounded-[24px] border border-[#eaecf0]"
                  loading="lazy"
                />
              </div>
            </section>

            <section id="why-teams-switch" className="mt-12 overflow-hidden rounded-[36px] border border-white/80 bg-white shadow-[0_30px_90px_rgba(15,23,42,0.12)]">
              <div className="brand-primary-panel px-8 py-7">
                <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-white/72">{copy.whyTeamsSwitch.eyebrow}</p>
                <h2 className="font-headline mt-3 text-3xl font-extrabold tracking-[-0.04em] text-white">{copy.whyTeamsSwitch.title}</h2>
                <p className="mt-3 text-sm leading-7 text-white/80">{copy.whyTeamsSwitch.description}</p>
              </div>

              <div className="space-y-4 px-6 py-6 sm:px-8 sm:py-8">
                {copy.whyTeamsSwitch.items.map(([title, detail]) => (
                  <div key={title} className="rounded-[24px] border border-[#eaecf0] bg-[#f8fafc] px-5 py-5">
                    <p className="text-sm font-bold text-[#101828]">{title}</p>
                    <p className="mt-2 text-sm leading-7 text-[#667085]">{detail}</p>
                  </div>
                ))}
              </div>
            </section>

            <section id="pricing" className="mt-12 rounded-[36px] border border-white/80 bg-white/80 p-6 shadow-[0_30px_90px_rgba(15,23,42,0.08)] backdrop-blur-sm sm:p-8">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                <div>
                  <div className="inline-flex rounded-full border border-[#d0d5dd] bg-[#f8fafc] px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.24em] text-[#475467]">{copy.pricing.eyebrow}</div>
                  <h2 className="font-headline mt-4 text-3xl font-extrabold tracking-[-0.04em] text-[#101828] sm:text-4xl">{copy.pricing.title}</h2>
                  <p className="mt-3 max-w-3xl text-sm leading-7 text-[#667085] sm:text-base">{copy.pricing.description}</p>
                </div>
                <p className="rounded-[22px] border border-[#d0d5dd] bg-white px-4 py-3 text-sm font-semibold text-[#344054] shadow-[0_12px_32px_rgba(15,23,42,0.05)]">{copy.pricing.note}</p>
              </div>

              <div className="mt-8 grid gap-4 lg:grid-cols-3">
                {copy.pricing.plans.map(plan => (
                  <article key={plan.name} className={["flex h-full flex-col rounded-[28px] border p-5 shadow-[0_20px_50px_rgba(15,23,42,0.05)]", plan.featured ? "border-[#6cdbab] bg-[linear-gradient(180deg,#ffffff_0%,#f3fff9_100%)]" : "border-[#eaecf0] bg-white"].join(" ")}>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-bold text-[#101828]">{plan.name}</p>
                        <p className="mt-2 text-sm leading-6 text-[#667085]">{plan.description}</p>
                      </div>
                      {plan.featured ? <span className="rounded-full bg-[#dcfae6] px-3 py-1 text-[11px] font-bold uppercase tracking-[0.18em] text-[#027a48]">{copy.actions.popular}</span> : null}
                    </div>

                    <div className="mt-4 flex items-end gap-1">
                      <span className="font-headline text-4xl font-extrabold tracking-[-0.06em] text-[#101828]">{plan.price}</span>
                      <span className="pb-1 text-sm font-medium text-[#667085]">{plan.cadence}</span>
                    </div>

                    <div className="mt-6 space-y-3 rounded-[22px] bg-[#f8fafc] px-4 py-4">
                      <div>
                        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#667085]">{copy.pricing.includedAllowance}</p>
                        <p className="mt-2 text-sm font-semibold leading-6 text-[#344054]">{plan.allowance}</p>
                      </div>
                      <div>
                        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#667085]">{copy.pricing.supportLabel}</p>
                        <p className="mt-2 text-sm font-semibold leading-6 text-[#344054]">{plan.support}</p>
                      </div>
                      <div>
                        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#667085]">{copy.pricing.retentionLabel}</p>
                        <p className="mt-2 text-sm font-semibold leading-6 text-[#344054]">{plan.retention}</p>
                      </div>
                    </div>

                    <div className="mt-auto pt-6">
                      <button type="button" onClick={() => navigateTo(config.signupHref)} className={["w-full rounded-[20px] px-4 py-3 text-sm font-bold uppercase tracking-[0.16em]", plan.featured ? "brand-primary-button" : "brand-primary-subtle-button"].join(" ")}>
                        {plan.ctaLabel}
                      </button>
                    </div>
                  </article>
                ))}
              </div>

              <div className="mt-8 overflow-hidden rounded-[28px] border border-[#eaecf0] bg-white shadow-[0_20px_50px_rgba(15,23,42,0.04)]">
                <div className="overflow-x-auto">
                  <table className="min-w-full border-separate border-spacing-0 text-left">
                    <thead>
                      <tr>
                        <th className="border-b border-[#eaecf0] bg-[#f8fafc] px-5 py-4 text-xs font-bold uppercase tracking-[0.18em] text-[#667085]">{copy.pricing.table.whatsIncluded}</th>
                        {copy.pricing.plans.map(plan => (
                          <th key={plan.name} className="border-b border-[#eaecf0] bg-[#f8fafc] px-5 py-4 text-sm font-bold text-[#101828]">{plan.name}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <th colSpan={copy.pricing.plans.length + 1} className="border-b border-[#eaecf0] bg-white px-5 py-3 text-xs font-bold uppercase tracking-[0.18em] text-[#98a2b3]">{copy.pricing.table.corePlatform}</th>
                      </tr>
                      {copy.pricing.coreFeatures.map(feature => (
                        <tr key={feature}>
                          <th className="border-b border-[#eaecf0] px-5 py-4 text-sm font-semibold text-[#344054]">{feature}</th>
                          {copy.pricing.plans.map(plan => (
                            <td key={`${feature}-${plan.name}`} className="border-b border-[#eaecf0] px-5 py-4">
                              <span className="inline-flex items-center gap-2 rounded-full bg-[#ecfdf3] px-3 py-1 text-sm font-semibold text-[#027a48]">
                                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#dcfae6] text-xs">✓</span>
                                {copy.actions.included}
                              </span>
                            </td>
                          ))}
                        </tr>
                      ))}

                      <tr>
                        <th colSpan={copy.pricing.plans.length + 1} className="border-b border-[#eaecf0] bg-white px-5 py-3 text-xs font-bold uppercase tracking-[0.18em] text-[#98a2b3]">{copy.pricing.table.allowanceAndSupport}</th>
                      </tr>
                      {copy.pricing.allowanceRows.map((row, rowIndex) => (
                        <tr key={row.label}>
                          <th className={["px-5 py-4 text-sm font-semibold text-[#344054]", rowIndex < copy.pricing.allowanceRows.length - 1 ? "border-b border-[#eaecf0]" : ""].join(" ")}>{row.label}</th>
                          {row.values.map((value, valueIndex) => (
                            <td key={`${row.label}-${value.primary}-${valueIndex}`} className={["px-5 py-4", rowIndex < copy.pricing.allowanceRows.length - 1 ? "border-b border-[#eaecf0]" : "", valueIndex === 1 ? "bg-[#fcfffd]" : ""].join(" ")}>
                              <p className="text-sm font-semibold text-[#344054]">{value.primary}</p>
                              {value.secondary ? <p className="mt-1 text-sm text-[#667085]">{value.secondary}</p> : null}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </section>

            <section id="savings-calculator" className="mt-12 overflow-hidden rounded-[36px] border border-[#d0d5dd] bg-[linear-gradient(180deg,#ffffff_0%,#f7fbff_100%)] shadow-[0_30px_90px_rgba(15,23,42,0.08)]">
              <div className="border-b border-[#eaecf0] px-6 py-7 sm:px-8">
                <div className="inline-flex rounded-full border border-[#d0d5dd] bg-white px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.24em] text-[#475467]">{copy.calculator.eyebrow}</div>
                <h2 className="font-headline mt-4 text-3xl font-extrabold tracking-[-0.04em] text-[#101828] sm:text-4xl">{copy.calculator.title}</h2>
                <p className="mt-3 max-w-3xl text-sm leading-7 text-[#667085] sm:text-base">{copy.calculator.description}</p>
              </div>

              <div className="grid gap-6 px-6 py-6 sm:px-8 sm:py-8 lg:grid-cols-[1.05fr_1.35fr]">
                <div className="space-y-4 rounded-[28px] border border-[#eaecf0] bg-white p-5 shadow-[0_20px_50px_rgba(15,23,42,0.04)]">
                  <div>
                    <p className="text-sm font-bold text-[#101828]">{copy.calculator.modelTitle}</p>
                    <p className="mt-2 text-sm leading-6 text-[#667085]">{copy.calculator.modelDescription}</p>
                  </div>

                  {calculatorFields.map(field => (
                    <label key={field.label} className="block rounded-[22px] bg-[#f8fafc] px-4 py-4">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-sm font-semibold text-[#344054]">{field.label}</p>
                          <p className="mt-1 text-sm text-[#667085]">{field.helper}</p>
                        </div>
                        <span className="rounded-full bg-white px-3 py-1 text-sm font-bold text-[#101828] shadow-[0_8px_20px_rgba(15,23,42,0.06)]">{formatCount(field.value, language)}</span>
                      </div>
                      <input
                        type="range"
                        min={field.min}
                        max={field.max}
                        step={field.step}
                        value={field.value}
                        onChange={(event) => {
                          field.onChange(Number(event.target.value));
                        }}
                        className="mt-4 h-2 w-full cursor-pointer appearance-none rounded-full bg-[#d0d5dd] accent-[#12b76a]"
                      />
                    </label>
                  ))}

                  <div className="rounded-[24px] border border-[#c7ead8] bg-[#ecfdf3] px-4 py-4">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#027a48]">{copy.calculator.currentEstimate}</p>
                    <div className="mt-2 flex items-end gap-2">
                      <span className="font-headline text-4xl font-extrabold tracking-[-0.06em] text-[#101828]">{formatCurrency(kuiiMonthlyCost, language)}</span>
                      <span className="pb-1 text-sm font-medium text-[#667085]">{language === "ja" ? "/月" : "/month"}</span>
                    </div>
                    <p className="mt-2 text-sm leading-6 text-[#475467]">{getEstimateDescription(language, kuiiMonthlyCost, agentCount)}</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="rounded-[28px] border border-[#d0d5dd] bg-white p-5 shadow-[0_20px_50px_rgba(15,23,42,0.04)]">
                    <p className="text-sm font-bold text-[#101828]">{copy.calculator.comparisonTitle}</p>
                    <p className="mt-2 text-sm leading-6 text-[#667085]">{getComparisonDescription(language, agentCount, ticketCount, articleCount, logCount)}</p>
                    <p className="mt-4 rounded-[20px] bg-[#f8fafc] px-4 py-3 text-sm font-medium text-[#344054]">
                      <span className="font-bold text-[#101828]">{getHighestSavingsText(language, biggestSavings.savings, biggestSavings.name)}</span>
                    </p>
                  </div>

                  <div className="grid gap-4 xl:grid-cols-3">
                    {providerEstimates.map(provider => (
                      <article key={provider.name} className="flex h-full flex-col rounded-[28px] border border-[#eaecf0] bg-white p-5 shadow-[0_20px_50px_rgba(15,23,42,0.04)]">
                        <div>
                          <p className="text-sm font-bold text-[#101828]">{provider.name}</p>
                          <p className="mt-2 text-sm leading-6 text-[#667085]">{provider.detail}</p>
                        </div>

                        <div className="mt-6">
                          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#667085]">{copy.calculator.providerMonthlyCost}</p>
                          <div className="mt-2 flex items-end gap-2">
                            <span className="font-headline text-4xl font-extrabold tracking-[-0.06em] text-[#101828]">{formatCurrency(provider.monthlyCost, language)}</span>
                            <span className="pb-1 text-sm font-medium text-[#667085]">{language === "ja" ? "/月" : "/month"}</span>
                          </div>
                        </div>

                        <div className="mt-6 rounded-[22px] bg-[#f8fafc] px-4 py-4">
                          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#667085]">{copy.calculator.providerSavings}</p>
                          <p className="mt-2 text-2xl font-extrabold tracking-[-0.04em] text-[#027a48]">{formatCurrency(provider.savings, language)}</p>
                          <p className="mt-2 text-sm leading-6 text-[#667085]">{provider.note}</p>
                        </div>
                      </article>
                    ))}
                  </div>

                  <p className="text-xs leading-6 text-[#667085]">{copy.calculator.disclaimer}</p>
                </div>
              </div>
            </section>
          </div>
        </div>
      </section>

      <footer className="border-t border-[#eaecf0] bg-white/90 backdrop-blur-sm">
        <div className="mx-auto flex max-w-7xl flex-col gap-6 px-6 py-8 sm:px-8 lg:flex-row lg:items-center lg:justify-between lg:px-10">
          <div>
            <p className="font-headline text-3xl font-extrabold tracking-[-0.08em] text-[#101828]">Kuii</p>
            <p className="mt-2 max-w-xl text-sm leading-6 text-[#667085]">{copy.footer.tagline}</p>
          </div>

          <div className="flex flex-wrap items-center gap-x-5 gap-y-3 text-sm font-semibold text-[#475467]">
            <a href={config.homeHref} className="transition hover:text-[#101828]">{copy.actions.home}</a>
            <a href={config.documentationHref} className="transition hover:text-[#101828]">{copy.actions.documentation}</a>
            <a href={config.apiHref} className="transition hover:text-[#101828]">{copy.actions.apiReference}</a>
            <a href={config.signupHref} className="transition hover:text-[#101828]">{copy.actions.startFree}</a>
            <a href={config.loginHref} className="transition hover:text-[#101828]">{copy.actions.signIn}</a>
            <a href={config.termsHref} className="transition hover:text-[#101828]">{copy.actions.termsOfService}</a>
            <a href={config.privacyHref} className="transition hover:text-[#101828]">{copy.actions.privacyPolicy}</a>
            <a href={config.refundPolicyHref} className="transition hover:text-[#101828]">{copy.actions.refundPolicy}</a>
          </div>
        </div>
      </footer>
    </main>
  );
}

function start() {
  const container = document.getElementById("root");

  if (!container) {
    return;
  }

  const root = window.__kuiiLandingRoot__ ?? createRoot(container);
  window.__kuiiLandingRoot__ = root;
  root.render(<LandingPage />);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", start);
} else {
  start();
}
